import { BellRing, Copy, Download, Mail, Send, Trash2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { InviteStats, SurveyDetail, SurveyInvite, SurveyInviteStatus } from '../api/types';
import { SurveyHeader } from '../components/SurveyHeader';
import { confirmAction, Empty, Field, Modal, PageLoader, useToast } from '../components/ui';
import { formatDate, formatNumber, relativeTime } from '../format';
import { useI18n } from '../i18n';

const STATUS_PILL: Record<SurveyInviteStatus, string> = {
  new: '',
  queued: 'blue',
  sent: 'yellow',
  opened: 'orange',
  completed: 'green',
  failed: 'red',
};

export function personalLink(code: string, token: string) {
  return `${window.location.origin}/s/${code}?invite=${encodeURIComponent(token)}`;
}

/** Parses pasted lines like "ana@example.com, Ana" or "Ana <ana@example.com>". */
export function parseInvitees(text: string): { email: string; name: string }[] {
  const out: { email: string; name: string }[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const angle = line.match(/^(.*)<([^>]+)>\s*$/);
    if (angle) {
      out.push({ email: angle[2]!.trim(), name: angle[1]!.trim().replace(/^"|"$/g, '') });
      continue;
    }
    const parts = line.split(/[,;\t]/).map((p) => p.trim().replace(/^"|"$/g, ''));
    const emailAt = parts.findIndex((p) => p.includes('@'));
    if (emailAt < 0) {
      out.push({ email: parts[0] ?? line, name: '' });
      continue;
    }
    out.push({ email: parts[emailAt]!, name: parts.filter((_, i) => i !== emailAt).join(' ').trim() });
  }
  return out;
}

function csvCell(v: string) {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Personal survey links for respondents, emailed with reminders for those who haven't answered. */
export function SurveyInvitesPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [data, setData] = useState<{ invites: SurveyInvite[]; stats: InviteStats; emailEnabled: boolean } | null>(null);
  const [inviteOnly, setInviteOnly] = useState(false);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [filter, setFilter] = useState<'' | SurveyInviteStatus>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getSurvey(id).then(setSurvey, (e) => setError(e instanceof ApiError ? e.message : t('common.load_failed')));
    api.collection(id).then((c) => setInviteOnly(c.inviteOnly), () => {});
  }, [id, t]);

  const canShare = survey?.permissions.includes('survey.share');
  const load = useCallback(
    (silent = false) =>
      api.surveyInvites(id).then(
        (d) => {
          setData(d);
          setSelected((cur) => cur.filter((x) => d.invites.some((i) => i.id === x)));
        },
        (e) => !silent && setError(e instanceof ApiError ? e.message : t('common.load_failed')),
      ),
    [id, t],
  );
  useEffect(() => {
    if (canShare) void load();
  }, [canShare, load]);

  // While emails are going out, refresh so statuses move on their own.
  const queued = data?.stats.queued ?? 0;
  useEffect(() => {
    if (!queued) return;
    const timer = window.setInterval(() => void load(true), 3000);
    return () => window.clearInterval(timer);
  }, [queued, load]);

  const shown = useMemo(() => (data?.invites ?? []).filter((i) => !filter || i.status === filter), [data, filter]);

  if (error) return <div className="banner error">{error}</div>;
  if (!survey) return <PageLoader />;
  if (!canShare) return <Navigate to={`/surveys/${id}/share`} replace />;

  const published = survey.status === 'published';
  const canEmail = !!data?.emailEnabled && published;

  async function run(action: () => Promise<string>) {
    setBusy(true);
    try {
      toast(await action());
      await load(true);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const send = (remind: boolean, ids?: string[]) =>
    run(async () => {
      const r = await api.sendSurveyInvites(id, remind, ids);
      return r.queued ? t(remind ? 'invites.reminders_queued' : 'invites.emails_queued', { count: r.queued }) : t('invites.nothing_to_send');
    });

  const remove = (ids: string[]) => {
    if (!confirmAction(t('invites.remove_confirm', { count: ids.length }))) return;
    void run(async () => {
      const r = await api.removeSurveyInvites(id, ids);
      setSelected([]);
      return t('invites.removed', { count: r.removed });
    });
  };

  async function copy(inv: SurveyInvite) {
    const url = personalLink(survey!.code, inv.token);
    try {
      await navigator.clipboard.writeText(url);
      toast(t('share.copied'));
    } catch {
      toast(t('invitations.copy_failed', { url }), 'error');
    }
  }

  function downloadCsv() {
    const rows = [['email', 'name', 'status', 'link'], ...(data?.invites ?? []).map((i) => [i.email, i.name, i.status, personalLink(survey!.code, i.token)])];
    const blob = new Blob(['﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${survey!.code}-invitations.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const allShown = shown.length > 0 && shown.every((i) => selected.includes(i.id));
  const toggle = (x: string) => setSelected((cur) => (cur.includes(x) ? cur.filter((y) => y !== x) : [...cur, x]));
  const stats = data?.stats;

  return (
    <>
      <SurveyHeader
        id={survey.id}
        number={survey.number}
        code={survey.code}
        status={survey.status}
        permissions={survey.permissions}
        active="invites"
        title={<h1 className="ellipsis" style={{ fontSize: 26, fontWeight: 500 }}>{survey.title || t('common.untitled_questionnaire')}</h1>}
        actions={
          <div className="btn-row">
            <button className="btn" onClick={downloadCsv} disabled={!data?.invites.length}>
              <Download /> {t('invites.download_csv')}
            </button>
            <button className="btn primary" onClick={() => setAdding(true)}>
              <UserPlus /> {t('invites.add')}
            </button>
          </div>
        }
      />
      <div className="stack gap-16">
        {data && !data.emailEnabled && <div className="banner">{t('invites.email_off')}</div>}
        {data?.emailEnabled && !published && <div className="banner warn">{t('invites.publish_first')}</div>}
        <div className="banner">
          <Mail />
          <span className="grow">{inviteOnly ? t('invites.mode_invite_only') : t('invites.mode_open')}</span>
          <Link className="btn sm" to={`/surveys/${id}/share`}>{t('invites.change_mode')}</Link>
        </div>

        {!data ? (
          <PageLoader />
        ) : data.invites.length === 0 ? (
          <div className="card">
            <Empty icon={<Mail />} title={t('invites.empty_title')}>
              <p className="small">{t('invites.empty_body')}</p>
              <button className="btn primary mt-8" onClick={() => setAdding(true)}>
                <UserPlus /> {t('invites.add')}
              </button>
            </Empty>
          </div>
        ) : (
          <div className="card">
            <div className="card-head" style={{ flexWrap: 'wrap', gap: 10 }}>
              <div className="btn-row grow" style={{ flexWrap: 'wrap' }}>
                {stats &&
                  (['total', 'new', 'sent', 'opened', 'completed', 'failed'] as const).map((k) => (
                    <button
                      key={k}
                      className={`pill ${k === 'total' ? '' : STATUS_PILL[k]}`}
                      style={{ cursor: 'pointer', outline: (filter || 'total') === k ? '2px solid var(--border-strong, var(--border))' : undefined }}
                      onClick={() => setFilter(k === 'total' ? '' : k)}
                    >
                      {t(`invites.status_${k}`)} · {formatNumber(stats[k])}
                    </button>
                  ))}
              </div>
              <div className="btn-row" style={{ flexWrap: 'wrap' }}>
                {selected.length > 0 ? (
                  <>
                    <span className="small muted">{t('invites.selected', { count: selected.length })}</span>
                    <button className="btn sm" disabled={busy || !canEmail} onClick={() => void send(false, selected)}>
                      <Send /> {t('invites.send')}
                    </button>
                    <button className="btn sm" disabled={busy || !canEmail} onClick={() => void send(true, selected)}>
                      <BellRing /> {t('invites.remind')}
                    </button>
                    <button className="btn sm danger" disabled={busy} onClick={() => remove(selected)}>
                      <Trash2 /> {t('invites.remove')}
                    </button>
                  </>
                ) : (
                  <>
                    <button className="btn sm" disabled={busy || !canEmail || !stats?.new} onClick={() => void send(false)}>
                      <Send /> {t('invites.send_all_new', { count: stats?.new ?? 0 })}
                    </button>
                    <button className="btn sm" disabled={busy || !canEmail} onClick={() => void send(true)}>
                      <BellRing /> {t('invites.remind_all')}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        aria-label={t('invites.select_all')}
                        checked={allShown}
                        onChange={() => setSelected(allShown ? selected.filter((x) => !shown.some((i) => i.id === x)) : [...new Set([...selected, ...shown.map((i) => i.id)])])}
                      />
                    </th>
                    <th>{t('invites.col_person')}</th>
                    <th>{t('invites.col_status')}</th>
                    <th>{t('invites.col_emailed')}</th>
                    <th>{t('invites.col_answered')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((i) => (
                    <tr key={i.id}>
                      <td>
                        <input type="checkbox" aria-label={i.email} checked={selected.includes(i.id)} onChange={() => toggle(i.id)} />
                      </td>
                      <td>
                        <div className="ellipsis">{i.name || i.email}</div>
                        {i.name && <div className="small muted ellipsis">{i.email}</div>}
                      </td>
                      <td>
                        <span className={`pill ${STATUS_PILL[i.status]}`} title={i.lastError ?? undefined}>
                          {t(`invites.status_${i.status}`)}
                        </span>
                        {i.lastError && <div className="small error-text ellipsis" style={{ maxWidth: 220 }}>{i.lastError}</div>}
                      </td>
                      <td className="small">
                        {i.sentAt ? <span title={formatDate(i.sentAt, true)}>{relativeTime(i.sentAt)}</span> : <span className="muted">—</span>}
                        {i.reminderCount > 0 && (
                          <div className="muted" title={formatDate(i.remindedAt, true)}>
                            {t('invites.reminded', { count: i.reminderCount })}
                          </div>
                        )}
                      </td>
                      <td className="small">
                        {i.completedAt ? (
                          <Link to={`/surveys/${id}/responses`} title={formatDate(i.completedAt, true)}>{relativeTime(i.completedAt)}</Link>
                        ) : i.openedAt ? (
                          <span className="muted">{t('invites.opened_at', { when: relativeTime(i.openedAt) })}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          <button className="btn sm ghost icon" title={t('invites.copy_link')} aria-label={t('invites.copy_link')} onClick={() => void copy(i)}>
                            <Copy />
                          </button>
                          {!i.completedAt && (
                            <button
                              className="btn sm ghost icon"
                              disabled={busy || !canEmail}
                              title={i.sentAt ? t('invites.remind') : t('invites.send')}
                              aria-label={i.sentAt ? t('invites.remind') : t('invites.send')}
                              onClick={() => void send(!!i.sentAt, [i.id])}
                            >
                              {i.sentAt ? <BellRing /> : <Send />}
                            </button>
                          )}
                          <button className="btn sm ghost icon" disabled={busy} title={t('invites.remove')} aria-label={t('invites.remove')} onClick={() => remove([i.id])}>
                            <Trash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {adding && (
        <AddInvitees
          surveyId={id}
          canEmail={canEmail}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            void load(true);
          }}
        />
      )}
    </>
  );
}

function AddInvitees({ surveyId, canEmail, onClose, onAdded }: { surveyId: string; canEmail: boolean; onClose: () => void; onAdded: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [text, setText] = useState('');
  const [send, setSend] = useState(canEmail);
  const [busy, setBusy] = useState(false);
  const people = parseInvitees(text);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!people.length) return;
    setBusy(true);
    try {
      const r = await api.addSurveyInvites(surveyId, people, send && canEmail);
      toast(t('invites.added', { added: r.added, skipped: r.skipped }));
      if (r.invalid.length) toast(t('invites.invalid_emails', { list: r.invalid.slice(0, 5).join(', ') }), 'error');
      onAdded();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('invites.add_title')}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn primary" type="submit" form="add-invitees" disabled={busy || !people.length}>
            <UserPlus /> {t('invites.add_count', { count: people.length })}
          </button>
        </>
      }
    >
      <form id="add-invitees" className="stack gap-12" onSubmit={(e) => void submit(e)}>
        <Field label={t('invites.paste_label')} hint={t('invites.paste_hint')} htmlFor="invitees">
          <textarea id="invitees" className="input" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={'ana@example.com, Ana Lestari\nBudi <budi@example.com>\ncici@example.com'} autoFocus />
        </Field>
        <label className="row gap-8 small">
          <input type="checkbox" checked={send && canEmail} disabled={!canEmail} onChange={(e) => setSend(e.target.checked)} />
          {canEmail ? t('invites.send_now') : t('invites.send_now_unavailable')}
        </label>
      </form>
    </Modal>
  );
}
